import React from 'react';
import { Plus, Trash, X, ArrowsClockwise, Play } from '@phosphor-icons/react';

export const RolloverPanel = ({
  classEntries, rolloverCandidates, rolloverSourceClasses,
  rolloverLoading, showRolloverPicker, selectedRolloverIds,
  onSetShowRolloverPicker, onSetSelectedRolloverIds,
  onAddRollovers, onRemoveRollover, onToggleRolloverSelection
}) => {
  const rolloverEntries = classEntries.filter(e => e.is_rollover);
  const directEntries = classEntries.filter(e => !e.is_rollover);
  const sourceNames = (rolloverSourceClasses || []).map(s => s.name).join(', ') || 'source class';

  // Group candidates by source class
  const candidatesBySource = {};
  (rolloverCandidates || []).forEach(c => {
    const key = c.source_class_id || 'unknown';
    if (!candidatesBySource[key]) candidatesBySource[key] = { name: c.source_class_name || 'Unknown', candidates: [] };
    candidatesBySource[key].candidates.push(c);
  });

  return (
    <div className="p-5" data-testid="rollovers-panel">
      <p className="text-xs text-[#9E9E9E] mb-4">
        Roll over entries from <span className="text-[#FF9500] font-semibold">{sourceNames}</span> into this class. Select individual riders to roll over.
      </p>

      <div className="flex items-center justify-between mb-3">
        <span className="text-sm font-semibold text-white">{rolloverEntries.length} Rollovers / {directEntries.length} Direct</span>
        <button
          onClick={() => { onSetShowRolloverPicker(true); onSetSelectedRolloverIds([]); }}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-[#FF9500]/15 text-[#FF9500] border border-[#FF9500]/30 rounded-md text-sm font-semibold hover:bg-[#FF9500]/25 transition-colors"
          data-testid="add-rollovers-btn"
        >
          <Plus size={13} weight="bold" /> Add Rollovers
        </button>
      </div>

      {showRolloverPicker && (
        <div className="mb-4 bg-[#0A0A0A] border border-[#FF9500]/30 rounded-lg p-4" data-testid="rollover-picker">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-sm font-bold text-[#FF9500]">Select riders to roll over</h4>
            <button onClick={() => onSetShowRolloverPicker(false)} className="p-1 hover:bg-[#262626] rounded">
              <X size={14} className="text-[#9E9E9E]" />
            </button>
          </div>
          {rolloverLoading ? (
            <div className="text-center py-4"><div className="shimmer h-6 w-6 rounded-full mx-auto"></div></div>
          ) : rolloverCandidates.length === 0 ? (
            <p className="text-[#9E9E9E] text-sm text-center py-4">No entries in source classes yet</p>
          ) : (
            <>
              <div className="max-h-[300px] overflow-y-auto space-y-3 mb-3">
                {Object.entries(candidatesBySource).map(([srcId, group]) => (
                  <div key={srcId}>
                    <div className="flex items-center justify-between mb-1.5">
                      <p className="text-xs font-bold text-[#FF9500]">{group.name}</p>
                      <button
                        onClick={() => {
                          const unrolled = group.candidates.filter(c => !c.already_rolled).map(c => c.id);
                          onSetSelectedRolloverIds(prev => [...new Set([...prev, ...unrolled])]);
                        }}
                        className="text-[10px] text-[#FF9500] hover:underline"
                        data-testid={`select-all-from-${srcId}`}
                      >
                        Select All
                      </button>
                    </div>
                    <div className="space-y-1">
                      {group.candidates.map((c) => (
                        <div key={c.id} onClick={() => !c.already_rolled && onToggleRolloverSelection(c.id)}
                          className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-colors ${
                            c.already_rolled ? 'opacity-40 cursor-not-allowed bg-[#161616]' :
                            selectedRolloverIds.includes(c.id) ? 'bg-[#FF9500]/10 border border-[#FF9500]/30 cursor-pointer' :
                            'bg-[#161616] border border-transparent hover:border-[#262626] cursor-pointer'
                          }`} data-testid={`rollover-candidate-${c.id}`}>
                          <div className={`w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                            c.already_rolled ? 'bg-[#34C759] border-[#34C759]' :
                            selectedRolloverIds.includes(c.id) ? 'bg-[#FF9500] border-[#FF9500]' : 'border-[#555]'
                          }`}>
                            {(c.already_rolled || selectedRolloverIds.includes(c.id)) && (
                              <svg width="10" height="8" viewBox="0 0 10 8" fill="none"><path d="M1 4L3.5 6.5L9 1" stroke="white" strokeWidth="2" strokeLinecap="round"/></svg>
                            )}
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="text-white text-sm font-medium truncate">{c.rider_name}</p>
                            {c.horse_name && <p className="text-[#9E9E9E] text-xs truncate">{c.horse_name}</p>}
                          </div>
                          {c.already_rolled && <span className="text-[#34C759] text-[10px] font-bold">ROLLED</span>}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex gap-2">
                <button onClick={onAddRollovers} disabled={selectedRolloverIds.length === 0} className="px-4 py-2 bg-[#FF9500] text-black rounded-md text-sm font-bold hover:bg-[#FF9500]/80 transition-colors disabled:opacity-40 disabled:cursor-not-allowed" data-testid="confirm-rollovers-btn">
                  Roll Over {selectedRolloverIds.length} {selectedRolloverIds.length === 1 ? 'Rider' : 'Riders'}
                </button>
                <button onClick={() => onSetSelectedRolloverIds(rolloverCandidates.filter(c => !c.already_rolled).map(c => c.id))} className="px-3 py-2 text-[#FF9500] text-sm font-semibold hover:bg-[#FF9500]/10 rounded-md transition-colors" data-testid="select-all-rollovers-btn">
                  Select All
                </button>
              </div>
            </>
          )}
        </div>
      )}

      <div className="max-h-[300px] overflow-y-auto space-y-1">
        {classEntries.map((entry) => {
          const sourceName = entry.rollover_source_class_name || (rolloverSourceClasses || []).find(s => s.id === entry.rollover_source_class_id)?.name || 'source';
          return (
            <div key={entry.id} className={`flex items-center justify-between px-3 py-2 rounded-lg ${
              entry.is_rollover ? 'bg-[#FF9500]/5 border border-[#FF9500]/20' : 'bg-[#0A0A0A] border border-[#262626]'
            }`} data-testid={`rollover-list-entry-${entry.id}`}>
              <div className="flex items-center gap-2 min-w-0">
                {entry.is_rollover ? (
                  <ArrowsClockwise size={14} weight="bold" className="text-[#FF9500] flex-shrink-0" />
                ) : (
                  <Play size={14} weight="fill" className="text-[#34C759] flex-shrink-0" />
                )}
                <div className="min-w-0">
                  <p className="text-white text-sm font-medium truncate">{entry.rider_name}</p>
                  <p className="text-[#9E9E9E] text-[10px]">
                    {entry.is_rollover ? `Rolled from ${sourceName}` : 'Direct entry'}
                  </p>
                </div>
              </div>
              {entry.is_rollover && (
                <button onClick={() => onRemoveRollover(entry.id)} className="p-1 hover:bg-[#FF3B30]/10 rounded transition-colors flex-shrink-0" title="Remove rollover" data-testid={`remove-rollover-${entry.id}`}>
                  <Trash size={13} className="text-[#FF3B30]" />
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};
